/**
 * WINGMAN INDOOR ROUTE RENDERER
 * IAM-M16B-R1.8 / R5.3 — Pathfinder Directional Wayfinding
 *
 * Dedicated Mapbox navigation experience layer for indoor route visualization.
 * Renders verified route geometry, directional chevrons along ordered path,
 * subtle travel pulse animation, and restrained destination halo.
 * Strictly forbidden from mutating camera authority or routing topology.
 */

import type {
  IndoorRouteVisualization,
  RouteInfoState,
} from './IndoorRouteVisualizationContracts';

export const INDOOR_ROUTE_SOURCE_ID = 'wingman-indoor-route';

export const INDOOR_ROUTE_LAYERS = {
  CASING: 'wingman-indoor-route-casing',
  LINE: 'wingman-indoor-route-line',
  TRAVEL_PULSE: 'wingman-indoor-route-travel-pulse',
  ARROWS: 'wingman-indoor-route-arrows',
  DEST_HALO: 'wingman-indoor-route-dest-halo',
  START_CIRCLE: 'wingman-indoor-route-start-circle',
  DEST_CIRCLE: 'wingman-indoor-route-dest-circle',
  LABELS: 'wingman-indoor-route-labels',
  TRANSITION_CIRCLE: 'wingman-indoor-route-transition-circle',
  TRANSITION_LABELS: 'wingman-indoor-route-transition-labels',
} as const;

export class IndoorRouteRenderer {
  private activeRoute: IndoorRouteVisualization | null = null;
  private currentFloor: number | null = null;

  // Animation Authority State (Single authority, cancelable handle)
  private animFrameId: number | null = null;
  private animStartTime: number = 0;
  private isAnimating: boolean = false;
  private animationLoopCount: number = 0;
  private currentMap: any = null;

  /**
   * Returns currently active visualization object or null.
   */
  public getActiveRoute(): IndoorRouteVisualization | null {
    return this.activeRoute;
  }

  /**
   * Returns whether travel signal animation loop is currently active.
   */
  public isAnimationActive(): boolean {
    return this.isAnimating;
  }

  /**
   * Returns active animation loop count (must be 0 or 1, never duplicate).
   */
  public getAnimationLoopCount(): number {
    return this.animationLoopCount;
  }

  /**
   * Returns lightweight route summary state for UI consumption.
   */
  public getRouteInfo(): RouteInfoState | null {
    if (!this.activeRoute) return null;
    return {
      routeId: this.activeRoute.routeId,
      originName: this.activeRoute.origin.label,
      destinationName: this.activeRoute.destination.label,
      distanceMeters: this.activeRoute.totalDistanceMeters,
      activeFloor: this.currentFloor ?? this.activeRoute.origin.level,
      transitionsCount: this.activeRoute.levelTransitions,
      floorsInvolved: this.activeRoute.levelsTraversed,
      transitions: this.activeRoute.transitions ?? [],
    };
  }

  /**
   * Renders the route visualization onto the Mapbox instance.
   * If viz is null, safely clears the current route.
   * Idempotent: reuses existing source if already registered.
   */
  public renderRoute(
    map: any,
    viz: IndoorRouteVisualization | null,
    activeFloor?: number
  ): void {
    if (!map) return;

    if (!viz) {
      this.clearRoute(map);
      return;
    }

    this.activeRoute = viz;
    if (activeFloor !== undefined) {
      this.currentFloor = activeFloor;
    } else if (viz.floorId !== undefined) {
      this.currentFloor = viz.floorId;
    } else if (viz.origin?.level !== undefined) {
      this.currentFloor = viz.origin.level;
    }

    const existingSource = map.getSource(INDOOR_ROUTE_SOURCE_ID);

    if (existingSource) {
      // Re-use source and push new data
      existingSource.setData(viz.geoJson);
    } else {
      // Create single dedicated GeoJSON source with lineMetrics enabled for hardware trim
      map.addSource(INDOOR_ROUTE_SOURCE_ID, {
        type: 'geojson',
        data: viz.geoJson,
        lineMetrics: true,
      });

      this.addLayers(map);
    }

    if (this.currentFloor !== null) {
      this.applyFloorFilter(map, this.currentFloor);
    }

    // Start single animated travel loop (stops any previous loop first)
    this.startAnimation(map);
  }

  /**
   * Updates floor filtering on all route layers according to active floor authority.
   */
  public setFloor(map: any, floorLevel: number): void {
    this.currentFloor = floorLevel;
    if (!map) return;
    this.applyFloorFilter(map, floorLevel);
  }

  /**
   * Switch the view to a specific floor involved in the active route.
   * Calls the provided floor authority (e.g. indoorMapController.setLevel) without creating a competing authority.
   */
  public switchToRouteFloor(map: any, targetLevel: number, floorAuthorityFn?: (level: number) => void): boolean {
    if (!this.activeRoute || !this.activeRoute.levelsTraversed.includes(targetLevel)) {
      return false;
    }
    if (floorAuthorityFn) {
      floorAuthorityFn(targetLevel);
    }
    this.setFloor(map, targetLevel);
    return true;
  }

  /**
   * Cleans up all route layers, sources, resets state, and halts animation.
   */
  public clearRoute(map: any): void {
    this.stopAnimation();
    this.activeRoute = null;
    this.currentFloor = null;

    if (!map) return;

    // Remove layers in reverse dependency order
    const layerIds = [
      INDOOR_ROUTE_LAYERS.TRANSITION_LABELS,
      INDOOR_ROUTE_LAYERS.TRANSITION_CIRCLE,
      INDOOR_ROUTE_LAYERS.LABELS,
      INDOOR_ROUTE_LAYERS.DEST_CIRCLE,
      INDOOR_ROUTE_LAYERS.START_CIRCLE,
      INDOOR_ROUTE_LAYERS.DEST_HALO,
      INDOOR_ROUTE_LAYERS.ARROWS,
      INDOOR_ROUTE_LAYERS.TRAVEL_PULSE,
      INDOOR_ROUTE_LAYERS.LINE,
      INDOOR_ROUTE_LAYERS.CASING,
    ];

    for (const layerId of layerIds) {
      if (map.getLayer && map.getLayer(layerId)) {
        map.removeLayer(layerId);
      }
    }

    if (map.getSource && map.getSource(INDOOR_ROUTE_SOURCE_ID)) {
      map.removeSource(INDOOR_ROUTE_SOURCE_ID);
    }
  }

  /**
   * Destroy renderer instance and release resources.
   */
  public destroy(map?: any): void {
    if (map) {
      this.clearRoute(map);
    } else {
      this.stopAnimation();
    }
  }

  /**
   * Attaches clean, premium navigation overlay layers into Mapbox Standard slot "top".
   */
  private addLayers(map: any): void {
    if (!map || !map.addLayer) return;

    // 1. Route Casing (subtle dark navy border for high contrast over light floors)
    if (!map.getLayer(INDOOR_ROUTE_LAYERS.CASING)) {
      map.addLayer({
        id: INDOOR_ROUTE_LAYERS.CASING,
        type: 'line',
        source: INDOOR_ROUTE_SOURCE_ID,
        slot: 'top',
        filter: ['==', ['get', 'kind'], 'route-line'],
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#0f172a',
          'line-width': 8,
          'line-opacity': 0.85,
        },
      });
    }

    // 2. Main Base Route Line (vibrant navigation blue authority)
    if (!map.getLayer(INDOOR_ROUTE_LAYERS.LINE)) {
      map.addLayer({
        id: INDOOR_ROUTE_LAYERS.LINE,
        type: 'line',
        source: INDOOR_ROUTE_SOURCE_ID,
        slot: 'top',
        filter: ['==', ['get', 'kind'], 'route-line'],
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#0284c7',
          'line-width': 5,
          'line-opacity': 0.95,
        },
      });
    }

    // 3. Subtle Travel Pulse Line (Layer 3 — travels Origin -> Destination along real route)
    if (!map.getLayer(INDOOR_ROUTE_LAYERS.TRAVEL_PULSE)) {
      map.addLayer({
        id: INDOOR_ROUTE_LAYERS.TRAVEL_PULSE,
        type: 'line',
        source: INDOOR_ROUTE_SOURCE_ID,
        slot: 'top',
        filter: ['==', ['get', 'kind'], 'route-line'],
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#38bdf8', // radiant cyan pulse
          'line-width': 5,
          'line-opacity': 0.9,
        },
      });
    }

    // 4. Directional Arrows / Chevrons (Layer 2 — Mapbox symbol line placement)
    if (!map.getLayer(INDOOR_ROUTE_LAYERS.ARROWS)) {
      map.addLayer({
        id: INDOOR_ROUTE_LAYERS.ARROWS,
        type: 'symbol',
        source: INDOOR_ROUTE_SOURCE_ID,
        slot: 'top',
        filter: ['==', ['get', 'kind'], 'route-line'],
        layout: {
          'symbol-placement': 'line',
          'symbol-spacing': 45,
          'text-field': '▶',
          'text-size': 9,
          'text-keep-upright': false, // strictly follow coordinate direction; never flip backwards
          'text-rotation-alignment': 'map',
          'text-pitch-alignment': 'map',
          'text-allow-overlap': true,
          'text-ignore-placement': true,
        },
        paint: {
          'text-color': '#e0f2fe',
          'text-halo-color': '#0284c7',
          'text-halo-width': 1,
          'text-opacity': 0.95,
        },
      });
    }

    // 5. Destination Halo (Layer 4 — gentle ambient pulse/emphasis at destination)
    if (!map.getLayer(INDOOR_ROUTE_LAYERS.DEST_HALO)) {
      map.addLayer({
        id: INDOOR_ROUTE_LAYERS.DEST_HALO,
        type: 'circle',
        source: INDOOR_ROUTE_SOURCE_ID,
        slot: 'top',
        filter: ['==', ['get', 'kind'], 'destination'],
        paint: {
          'circle-radius': 14,
          'circle-color': '#0284c7',
          'circle-opacity': 0.25,
          'circle-stroke-width': 1.5,
          'circle-stroke-color': '#38bdf8',
          'circle-stroke-opacity': 0.6,
        },
      });
    }

    // 6. Start Point Marker (emerald green circle)
    if (!map.getLayer(INDOOR_ROUTE_LAYERS.START_CIRCLE)) {
      map.addLayer({
        id: INDOOR_ROUTE_LAYERS.START_CIRCLE,
        type: 'circle',
        source: INDOOR_ROUTE_SOURCE_ID,
        slot: 'top',
        filter: ['==', ['get', 'kind'], 'start'],
        paint: {
          'circle-radius': 7,
          'circle-color': '#22c55e',
          'circle-stroke-width': 2.5,
          'circle-stroke-color': '#ffffff',
          'circle-opacity': 1,
        },
      });
    }

    // 7. Destination Marker (deep blue circle with crisp white ring)
    if (!map.getLayer(INDOOR_ROUTE_LAYERS.DEST_CIRCLE)) {
      map.addLayer({
        id: INDOOR_ROUTE_LAYERS.DEST_CIRCLE,
        type: 'circle',
        source: INDOOR_ROUTE_SOURCE_ID,
        slot: 'top',
        filter: ['==', ['get', 'kind'], 'destination'],
        paint: {
          'circle-radius': 8,
          'circle-color': '#0284c7',
          'circle-stroke-width': 3,
          'circle-stroke-color': '#ffffff',
          'circle-opacity': 1,
        },
      });
    }

    // 8. Vertical Transition Marker (amber / gold circle)
    if (!map.getLayer(INDOOR_ROUTE_LAYERS.TRANSITION_CIRCLE)) {
      map.addLayer({
        id: INDOOR_ROUTE_LAYERS.TRANSITION_CIRCLE,
        type: 'circle',
        source: INDOOR_ROUTE_SOURCE_ID,
        slot: 'top',
        filter: ['==', ['get', 'kind'], 'transition'],
        paint: {
          'circle-radius': 8,
          'circle-color': '#f59e0b',
          'circle-stroke-width': 2.5,
          'circle-stroke-color': '#ffffff',
          'circle-opacity': 1,
        },
      });
    }

    // 9. Transition Labels
    if (!map.getLayer(INDOOR_ROUTE_LAYERS.TRANSITION_LABELS)) {
      map.addLayer({
        id: INDOOR_ROUTE_LAYERS.TRANSITION_LABELS,
        type: 'symbol',
        source: INDOOR_ROUTE_SOURCE_ID,
        slot: 'top',
        filter: ['==', ['get', 'kind'], 'transition'],
        layout: {
          'text-field': ['get', 'label'],
          'text-size': 11,
          'text-offset': [0, 1.4],
          'text-anchor': 'top',
          'text-allow-overlap': true,
          'text-ignore-placement': true,
        },
        paint: {
          'text-color': '#b45309',
          'text-halo-color': '#ffffff',
          'text-halo-width': 2,
        },
      });
    }

    // 10. Waypoint & Endpoint Labels
    if (!map.getLayer(INDOOR_ROUTE_LAYERS.LABELS)) {
      map.addLayer({
        id: INDOOR_ROUTE_LAYERS.LABELS,
        type: 'symbol',
        source: INDOOR_ROUTE_SOURCE_ID,
        slot: 'top',
        filter: ['in', ['get', 'kind'], ['literal', ['start', 'destination']]],
        layout: {
          'text-field': ['get', 'label'],
          'text-size': 12,
          'text-offset': [0, 1.3],
          'text-anchor': 'top',
          'text-allow-overlap': true,
          'text-ignore-placement': true,
        },
        paint: {
          'text-color': '#0f172a',
          'text-halo-color': '#ffffff',
          'text-halo-width': 2,
        },
      });
    }
  }

  /**
   * Applies floor filter expression to all route layers while preserving layer kind.
   */
  private applyFloorFilter(map: any, floorLevel: number): void {
    if (!map || !map.setFilter) return;

    const floorCondition = [
      'any',
      ['==', ['get', 'level'], floorLevel],
      ['!', ['has', 'level']],
    ];

    const layerFilters: Record<string, any> = {
      [INDOOR_ROUTE_LAYERS.CASING]: ['all', ['==', ['get', 'kind'], 'route-line'], floorCondition],
      [INDOOR_ROUTE_LAYERS.LINE]: ['all', ['==', ['get', 'kind'], 'route-line'], floorCondition],
      [INDOOR_ROUTE_LAYERS.TRAVEL_PULSE]: ['all', ['==', ['get', 'kind'], 'route-line'], floorCondition],
      [INDOOR_ROUTE_LAYERS.ARROWS]: ['all', ['==', ['get', 'kind'], 'route-line'], floorCondition],
      [INDOOR_ROUTE_LAYERS.DEST_HALO]: ['all', ['==', ['get', 'kind'], 'destination'], floorCondition],
      [INDOOR_ROUTE_LAYERS.START_CIRCLE]: ['all', ['==', ['get', 'kind'], 'start'], floorCondition],
      [INDOOR_ROUTE_LAYERS.DEST_CIRCLE]: ['all', ['==', ['get', 'kind'], 'destination'], floorCondition],
      [INDOOR_ROUTE_LAYERS.TRANSITION_CIRCLE]: ['all', ['==', ['get', 'kind'], 'transition'], floorCondition],
      [INDOOR_ROUTE_LAYERS.TRANSITION_LABELS]: ['all', ['==', ['get', 'kind'], 'transition'], floorCondition],
      [INDOOR_ROUTE_LAYERS.LABELS]: ['all', ['in', ['get', 'kind'], ['literal', ['start', 'destination']]], floorCondition],
    };

    for (const [layerId, filter] of Object.entries(layerFilters)) {
      if (map.getLayer && map.getLayer(layerId)) {
        map.setFilter(layerId, filter);
      }
    }
  }

  /**
   * Starts subtle travel pulse and destination halo animation loop.
   * Strictly respects prefers-reduced-motion.
   * Single animation authority: guarantees exactly one loop runs.
   */
  public startAnimation(map: any): void {
    this.stopAnimation();

    if (!map) return;

    // Respect user's accessibility preferences
    if (typeof window !== 'undefined' && window.matchMedia) {
      try {
        const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (prefersReduced) {
          return;
        }
      } catch {
        // Safe fallback
      }
    }

    this.isAnimating = true;
    this.animationLoopCount = 1;
    this.currentMap = map;
    this.animStartTime = typeof performance !== 'undefined' ? performance.now() : Date.now();

    const animate = (timestamp: number) => {
      if (!this.isAnimating || !this.currentMap) {
        return;
      }

      try {
        const elapsed = timestamp - this.animStartTime;
        const cycleMs = 2400; // ambient, restrained 2.4s cycle
        const t = (elapsed % cycleMs) / cycleMs;

        if (typeof this.currentMap.setPaintProperty === 'function') {
          // 1. Subtle travel pulse traveling Origin -> Destination along real route
          const pulseSpan = 0.22;
          const head = t * (1 + pulseSpan);
          const startOffset = Math.max(0, head - pulseSpan);
          const endOffset = Math.min(1, Math.max(0, head));

          try {
            if (this.currentMap.getLayer && this.currentMap.getLayer(INDOOR_ROUTE_LAYERS.TRAVEL_PULSE)) {
              this.currentMap.setPaintProperty(INDOOR_ROUTE_LAYERS.TRAVEL_PULSE, 'line-trim-offset', [startOffset, endOffset]);
              const pulseOpacity = (startOffset >= 1 || endOffset <= 0) ? 0 : 0.95;
              this.currentMap.setPaintProperty(INDOOR_ROUTE_LAYERS.TRAVEL_PULSE, 'line-opacity', pulseOpacity);
            }
          } catch {
            // graceful fallback if line-trim-offset not supported by mock or driver
          }

          // 2. Destination halo gentle ambient breathing pulse
          try {
            if (this.currentMap.getLayer && this.currentMap.getLayer(INDOOR_ROUTE_LAYERS.DEST_HALO)) {
              const haloRadius = 12 + 4 * Math.sin(t * Math.PI * 2);
              const haloOpacity = 0.22 + 0.14 * Math.sin(t * Math.PI * 2);
              this.currentMap.setPaintProperty(INDOOR_ROUTE_LAYERS.DEST_HALO, 'circle-radius', haloRadius);
              this.currentMap.setPaintProperty(INDOOR_ROUTE_LAYERS.DEST_HALO, 'circle-opacity', haloOpacity);
            }
          } catch {
            // ignore
          }
        }
      } catch {
        // Defensive: animation failure must NEVER degrade route visibility or crash
      }

      if (this.isAnimating) {
        if (typeof requestAnimationFrame !== 'undefined') {
          this.animFrameId = requestAnimationFrame(animate);
        }
      }
    };

    if (typeof requestAnimationFrame !== 'undefined') {
      this.animFrameId = requestAnimationFrame(animate);
    }
  }

  /**
   * Deterministically stops and cancels the animation loop.
   */
  public stopAnimation(): void {
    this.isAnimating = false;
    this.animationLoopCount = 0;
    if (this.animFrameId !== null) {
      if (typeof cancelAnimationFrame !== 'undefined') {
        cancelAnimationFrame(this.animFrameId);
      }
      this.animFrameId = null;
    }
    this.currentMap = null;
  }
}

export const indoorRouteRenderer = new IndoorRouteRenderer();
